import { Box, Typography } from '@mui/material';
import { vortxColors } from '@vortx/shared-ui';

const AGENT_ICONS = {
  battery: '🔋',
  ev: '🚛',
  cold: '❄️',
  solar: '☀️',
};

const NODE_WIDTH = 120;
const NODE_HEIGHT = 60;
const PADDING = 30;
const ARROW_GAP = 60;

/**
 * AgentDiagram — SVG node-link visualization of coordination topology.
 *
 * Proposers (flexibility < threshold) on the left, reactors on the right,
 * with arrows showing coordination flow and DC allocation percentages.
 */
export default function AgentDiagram({ agents = [], coordinationConfig = {} }) {
  const {
    coordination_mode = 'hybrid',
    agent_dc_fraction = {},
    flexibility_threshold = 0.7,
    flexibility_fractions = {},
  } = coordinationConfig;

  const proposers = agents.filter(a => {
    const flex = flexibility_fractions[a] ?? 0.5;
    return flex < flexibility_threshold;
  });

  const reactors = agents.filter(a => {
    const flex = flexibility_fractions[a] ?? 0.5;
    return flex >= flexibility_threshold;
  });

  const maxSide = Math.max(proposers.length, reactors.length, 1);
  const svgHeight = maxSide * (NODE_HEIGHT + PADDING) + PADDING + 30;
  const svgWidth = 2 * NODE_WIDTH + ARROW_GAP + 2 * PADDING + 100;

  const proposerX = PADDING;
  const reactorX = PADDING + NODE_WIDTH + ARROW_GAP + 100;

  const renderNode = (agent, x, y, isProposer) => {
    const fill = isProposer ? vortxColors.teal : vortxColors.gold;
    const dcFrac = agent_dc_fraction[agent];
    const dcLabel = dcFrac != null ? `DC: ${Math.round(dcFrac * 100)}%` : '';
    const icon = AGENT_ICONS[agent] || '⚡';

    return (
      <g key={`${agent}-${isProposer ? 'p' : 'r'}`}>
        <rect
          x={x} y={y}
          width={NODE_WIDTH} height={NODE_HEIGHT}
          rx={10} ry={10}
          fill={fill} opacity={0.9}
        />
        <text
          x={x + NODE_WIDTH / 2} y={y + 24}
          textAnchor="middle" fill={vortxColors.white}
          fontSize={14} fontWeight={600}
        >
          {icon} {agent.charAt(0).toUpperCase() + agent.slice(1)}
        </text>
        {dcLabel && (
          <text
            x={x + NODE_WIDTH / 2} y={y + NODE_HEIGHT + 16}
            textAnchor="middle" fill={vortxColors.stone}
            fontSize={11}
          >
            {dcLabel}
          </text>
        )}
      </g>
    );
  };

  const renderArrows = () => {
    const arrows = [];
    proposers.forEach((p, pi) => {
      const py = PADDING + pi * (NODE_HEIGHT + PADDING) + NODE_HEIGHT / 2;
      reactors.forEach((r, ri) => {
        const ry = PADDING + ri * (NODE_HEIGHT + PADDING) + NODE_HEIGHT / 2;
        const x1 = proposerX + NODE_WIDTH + 4;
        const x2 = reactorX - 4;
        arrows.push(
          <line
            key={`${p}-${r}`}
            x1={x1} y1={py} x2={x2} y2={ry}
            stroke={vortxColors.stone} strokeWidth={1.5}
            markerEnd="url(#arrowhead)"
          />
        );
      });
    });
    return arrows;
  };

  if (agents.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ p: 2 }}>
        No agents configured.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, color: vortxColors.stone }}>
        Coordination Topology
      </Typography>
      <svg
        width={svgWidth}
        height={svgHeight + 20}
        viewBox={`0 0 ${svgWidth} ${svgHeight + 20}`}
        style={{ maxWidth: '100%' }}
      >
        <defs>
          <marker
            id="arrowhead" markerWidth="8" markerHeight="6"
            refX="8" refY="3" orient="auto"
          >
            <polygon points="0 0, 8 3, 0 6" fill={vortxColors.stone} />
          </marker>
        </defs>

        {/* Column labels */}
        <text x={proposerX + NODE_WIDTH / 2} y={14} textAnchor="middle"
          fill={vortxColors.teal} fontSize={11} fontWeight={600}>
          Proposers
        </text>
        <text x={reactorX + NODE_WIDTH / 2} y={14} textAnchor="middle"
          fill={vortxColors.gold} fontSize={11} fontWeight={600}>
          Reactors
        </text>

        {/* Center label */}
        <text
          x={svgWidth / 2} y={svgHeight / 2}
          textAnchor="middle" fill={vortxColors.stone}
          fontSize={10} fontStyle="italic"
        >
          Hybrid Best-Response
        </text>

        {/* Nodes */}
        {proposers.map((a, i) =>
          renderNode(a, proposerX, PADDING + i * (NODE_HEIGHT + PADDING), true)
        )}
        {reactors.map((a, i) =>
          renderNode(a, reactorX, PADDING + i * (NODE_HEIGHT + PADDING), false)
        )}

        {/* Arrows */}
        {renderArrows()}
      </svg>
    </Box>
  );
}
